<template>
  <CustomTitleBar title="详细资料" />
  <div class="page-content">
    <!-- 头像 -->
    <van-cell title="头像" is-link>
      <template #value>
        <van-uploader v-model="fileList" :max-count="1" :after-read="afterRead">
          <img v-if="formData.avatar" :src="formData.avatar" class="avatar" />
          <van-icon v-else name="photograph" size="44" />
        </van-uploader>
      </template>
    </van-cell>

    <!-- 昵称 -->
    <van-cell title="昵称" :value="formData.nickName" is-link @click="showNamePopup = true" />

    <!-- 账号 -->
    <van-cell title="账号" :value="userInfo.onlyCode" />

    <!-- 手机号 -->
    <van-cell title="手机号" :value="formatPhone(userInfo.phoneNumber)" />

    <!-- 个性签名 -->
    <van-cell title="个性签名" is-link @click="showSignaturePopup = true">
      <template #value>
        <div class="signature-value">{{ formData.slogan || '' }}</div>
      </template>
    </van-cell>

    <!-- 性别 -->
    <van-cell title="性别" :value="genderText" is-link @click="showGenderPicker = true" />

    <!-- 修改昵称弹窗 -->
    <van-popup v-model:show="showNamePopup" position="bottom" round class="edit-popup">
      <div class="popup-header">
        <span>修改昵称</span>
        <van-icon name="cross" @click="showNamePopup = false" />
      </div>
      <div class="popup-content">
        <div class="input-wrapper">
          <input v-model="formData.nickName" type="text" placeholder="请输入昵称" class="custom-input" />
        </div>
        <div class="field-tip">仅限中文、字母、数字和下划线</div>
        <van-button block type="primary" class="confirm-btn" @click="onNameConfirm">确定</van-button>
      </div>
    </van-popup>

    <!-- 个性签名编辑弹窗 -->
    <van-popup v-model:show="showSignaturePopup" position="bottom" round class="edit-popup">
      <div class="popup-header">
        <span>修改个性签名</span>
        <van-icon name="cross" @click="showSignaturePopup = false" />
      </div>
      <div class="popup-content">
        <div class="input-wrapper">
          <input v-model="formData.slogan" type="text" placeholder="请输入个性签名" class="custom-input" />
        </div>
        <div class="field-tip">展示你的个性签名</div>
        <van-button block type="primary" class="confirm-btn" @click="onSignatureConfirm">确定</van-button>
      </div>
    </van-popup>

    <!-- 性别选择器 -->
    <van-popup v-model:show="showGenderPicker" position="bottom" round class="edit-popup">
      <div class="popup-header">选择性别</div>
      <van-picker :columns="genderOptions" @confirm="onGenderConfirm" @cancel="showGenderPicker = false" show-toolbar />
    </van-popup>
  </div>
</template>

<script lang="ts" setup>
import CustomTitleBar from "@/components/CustomTitleBar/index.vue";
import { ref, computed, onMounted } from "vue";
import { showToast } from "vant";
import type { UploaderFileListItem, UploaderAfterRead } from "vant";
import { uploadAvatar, modifyUserInfo } from "@/api/system";
import { useStore } from "vuex";

const store = useStore();

// 简化的表单数据接口
interface FormData {
  avatar: string;
  nickName: string;
  slogan: string;
  gender: number;
}

const fileList = ref<UploaderFileListItem[]>([]);
const showNamePopup = ref(false);
const showSignaturePopup = ref(false);
const showGenderPicker = ref(false);

const userInfo = ref<any>({});

const formData = ref({
  avatar: '',
  gender: 0,
  nickName: '',
  slogan: '',
});

const genderOptions = [
  { text: '男', value: 0 },
  { text: '女', value: 1 }
];

// 修改性别显示逻辑
const genderText = computed(() => {
  switch (formData.value.gender) {
    case 0: return '男';
    case 1: return '女';
    default: return '';
  }
});

// 修改手机号格式化方法
const formatPhone = (phone: string) => {
  // 去掉前面的区号86，然后格式化手机号
  const phoneNumber = phone?.replace(/^86/, '') || '';
  return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1****$3');
};

// 获取用户信息
const getUserInfoMethod = () => {
  const storageUserInfo = localStorage.getItem('userInfo');
  if (storageUserInfo) {
    const parsedUserInfo = JSON.parse(storageUserInfo);
    userInfo.value = parsedUserInfo;
    formData.value = {
      avatar: parsedUserInfo.avatar,
      nickName: parsedUserInfo.nickName,
      slogan: parsedUserInfo.slogan,
      gender: parsedUserInfo.gender,
    };
  }
};

// 头像上传
const afterRead: UploaderAfterRead = async (file) => {
  if (Array.isArray(file)) return;

  await uploadAvatar({ file: file.file as File }).then(res => {
    if (res.code === 200) {
      formData.value.avatar = res.data.avatar;
      store.dispatch('user/refreshUserInfo');
    }
  });
};

// 确认修改昵称
const onNameConfirm = () => {
  if (!formData.value.nickName.trim()) {
    showToast('昵称不能为空');
    return;
  }
  modifyUserInfo({
    nickName: formData.value.nickName
  }).then(res => {
    if (res.code === 200) {
      store.dispatch('user/refreshUserInfo');
      showNamePopup.value = false;
    } else {
      showToast(res.msg);
    }
  });
};

// 确认修改个性签名
const onSignatureConfirm = () => {
  modifyUserInfo({
    slogan: formData.value.slogan
  }).then(res => {
    if (res.code === 200) {
      store.dispatch('user/refreshUserInfo');
      showSignaturePopup.value = false;
    } else {
      showToast(res.msg);
    }
  });
};

// 确认性别选择
const onGenderConfirm = (value: any) => {
  formData.value.gender = Number(value.selectedValues[0]);
  modifyUserInfo({
    gender: Number(formData.value.gender)
  }).then(res => {
    if (res.code === 200) {
      store.dispatch('user/refreshUserInfo');
      showGenderPicker.value = false;
    } else {
      showToast(res.msg);
    }
  });
};

onMounted(() => {
  // 获取用户信息
  getUserInfoMethod();
});
</script>

<style scoped>
/* 页面基础样式 */
.page-content {
  min-height: 100vh;
  background-color: #f7f8fa;
  padding-top: 56px;
}

/* 头像相关样式 */
.avatar {
  width: 48px;
  height: 48px;
  border-radius: 4px;
  object-fit: cover;
}

/* Cell 样式覆盖 */
:deep(.van-cell) {
  align-items: center;
  padding: 16px;
  font-size: 16px;
  line-height: 24px;
  min-height: 64px;
}

:deep(.van-cell__title) {
  flex: none;
  width: 80px;
  color: #323233;
}

:deep(.van-cell__value) {
  color: #333333;
  text-align: right;
  flex: 1;
  margin-right: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

:deep(.van-cell__right-icon) {
  flex-shrink: 0;
}

/* 签名值样式 */
.signature-value {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}

/* 弹窗基础样式 */
.edit-popup {
  height: 35vh;
  background-color: #f5f5f5;
}

/* 弹窗头部样式 */
.popup-header {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 56px;
  font-size: 17px;
  font-weight: 500;
  background-color: #fff;
  border-radius: 16px 16px 0 0;
}

.popup-header .van-icon {
  position: absolute;
  right: 16px;
  font-size: 20px;
  color: #999;
}

/* 弹窗内容区域样式 */
.popup-content {
  padding: 20px 16px;
}

/* 输入框容器样式 */
.input-wrapper {
  background: #EEEEEE;
  border-radius: 8px;
  padding: 4px 16px;
  margin-bottom: 8px;
}

/* 自定义输入框样式 */
.custom-input {
  width: 100%;
  height: 44px;
  border: none;
  background: transparent;
  font-size: 16px;
  color: #333;
  outline: none;
}

.custom-input::placeholder {
  color: #999;
}

/* 提示文字样式 */
.field-tip {
  color: #999;
  font-size: 13px;
  margin: 8px 0 20px;
  padding-left: 4px;
}

/* 确认按钮样式 */
.confirm-btn {
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
  background: #007fff;
  border: none;
}

/* Uploader 样式覆盖 */
:deep(.van-uploader) {
  display: flex;
  justify-content: flex-end;
}

:deep(.van-uploader__wrapper) {
  margin: 0;
}

:deep(.van-uploader__upload) {
  margin: 0;
  width: 48px !important;
  height: 48px !important;
}

/* Popup 样式覆盖 */
:deep(.van-popup) {
  border-radius: 16px 16px 0 0;
}
</style>
